import React, { useEffect, useMemo } from 'react';

import { useAdvancedMarker } from '../TTDrawingManager';
import { createIconElement } from './iconUtils';

export interface AdvancedMarkerWrapperProps {
  map?: google.maps.Map;
  position: google.maps.LatLngLiteral;
  content?: HTMLElement | SVGElement;
  icon?: any;
  shadowIcon?: any;
  iconHighlight?: any;
  draggable?: boolean;
  hoverable?: boolean;
  visible?: boolean;
  isVisible?: boolean; // Alias for visible
  zIndex?: number;
  onClick?: () => void;
  onMouseEnter?: () => void;
  onMouseLeave?: () => void;
  onDragEnd?: (position: google.maps.LatLngLiteral) => void;
  onLoad?: () => void;
}

export const AdvancedMarkerWrapper: React.FC<AdvancedMarkerWrapperProps> = ({
  map,
  position,
  content,
  icon,
  shadowIcon,
  iconHighlight,
  draggable = false,
  visible = true,
  isVisible = true,
  zIndex,
  hoverable = false,
  onClick,
  onMouseEnter,
  onMouseLeave,
  onDragEnd,
  onLoad,
}) => {
  const actuallyVisible = visible && isVisible;

  // Create marker content from icons if no custom content is provided
  const markerContent = useMemo(() => {
    if (content) return content;
    if (!icon && !shadowIcon && !iconHighlight) return undefined;

    const div = document.createElement('div');
    div.style.position = 'relative';
    div.style.cursor = 'pointer';

    // Helper function to create icon element with positioning
    const createPositionedIconElement = (iconConfig: any, offsetZ?: number) => {
      const element = createIconElement(iconConfig, iconConfig?.scale || 1);
      if (!element) return null;

      if (offsetZ) {
        element.style.position = 'absolute';
        element.style.zIndex = offsetZ.toString();
      } else {
        element.style.position = 'relative';
        element.style.zIndex = '1';
      }

      // Handle anchor point for positioning
      if (iconConfig?.anchor) {
        element.style.transform = `translate(-${iconConfig.anchor.x}px, -${iconConfig.anchor.y}px)`;
      }

      return element;
    };

    // Add shadow icon if provided (lowest layer)
    if (shadowIcon) {
      const shadowElement = createPositionedIconElement(shadowIcon, 1);
      if (shadowElement) div.appendChild(shadowElement);
    }

    // Add highlight icon if provided (middle layer)
    if (iconHighlight) {
      const highlightElement = createPositionedIconElement(iconHighlight, 2);
      if (highlightElement) div.appendChild(highlightElement);
    }

    // Add main icon (top layer)
    if (icon) {
      const mainElement = createPositionedIconElement(icon, 3);
      if (mainElement) div.appendChild(mainElement);
    }

    // Note: do not attach DOM listeners here. We attach marker-level listeners
    // below so the event handling is consistent for AdvancedMarkerElement and
    // legacy google.maps.Marker.

    return div;
    // Exclude handler props from dependencies because we attach handlers on the
    // marker instance (not the DOM content). Keep visual deps only.
  }, [content, icon, shadowIcon, iconHighlight]);

  const marker = useAdvancedMarker({
    map,
    position,
    content: markerContent as HTMLElement,
    draggable,
    visible: actuallyVisible,
    zIndex,
    onDragEnd,
  });

  useEffect(() => {
    if (!marker || !onClick) return undefined;

    const handleClick = () => onClick();

    if (marker.addListener) {
      const listener = marker.addListener('click', handleClick);
      return () => listener?.remove();
    }
    if ((marker as any).setMap) {
      const listener = google.maps.event.addListener(marker, 'click', handleClick);
      return () => google.maps.event.removeListener(listener);
    }

    return undefined;
  }, [marker, onClick]);

  useEffect(() => {
    if (!marker || !onMouseEnter || !hoverable) return undefined;

    const mouseEnterListener = () => onMouseEnter();

    if (marker.addEventListener) {
      marker.addEventListener('mouseenter', mouseEnterListener);
      return () => marker.removeEventListener('mouseenter', mouseEnterListener);
    }
    if (marker.setMap) {
      const listener = google.maps.event.addListener(marker, 'mouseenter', mouseEnterListener);
      return () => google.maps.event.removeListener(listener);
    }

    return undefined;
  }, [marker, hoverable, onMouseEnter]);

  useEffect(() => {
    if (!marker || !onMouseLeave || !hoverable) return undefined;

    const mouseLeaveListener = () => onMouseLeave();

    if (marker.addEventListener) {
      marker.addEventListener('mouseleave', mouseLeaveListener);
      return () => marker.removeEventListener('mouseleave', mouseLeaveListener);
    }
    if (marker.setMap) {
      const listener = google.maps.event.addListener(marker, 'mouseleave', mouseLeaveListener);
      return () => google.maps.event.removeListener(listener);
    }

    return undefined;
  }, [marker, hoverable, onMouseLeave]);

  // Notify parent when marker is loaded
  useEffect(() => {
    if (marker && onLoad) {
      onLoad();
    }
  }, [marker, onLoad]);

  return null;
};
